import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const variantJson = target?.querySelector('variant-picker script[type="application/json"]')?.textContent;
    try {
      this.#variantMediaId = variantJson ? String(JSON.parse(variantJson).featured_media?.id ?? '') : '';
    } catch {
      this.#variantMediaId = '';
    }
  }

  #controller = new AbortController();

  /** The media id of the selected variant, so that a change which keeps the same photo leaves the gallery alone. */
  #variantMediaId = '';

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Slides to the photo of the option that was just picked. This runs on the click itself, so the photo
   * does not wait for the variant update to come back from the server.
   *
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const optionValueId = event.detail.resource?.id;
    if (!optionValueId || !(event.target instanceof Element)) return;

    const option = event.target.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!(option instanceof HTMLElement)) return;

    // Only an option that changes the photo may move the gallery. A swatch app re-clicks the size after a colour
    // change; that size still points at a variant of the old colour, so acting on it slid back to the old photo.
    if (!this.#optionChangesMedia(option)) return;

    const variantId = option.dataset.variantId;
    const mediaId = variantId ? this.#mediaIdOfVariant(variantId) : undefined;

    if (mediaId) this.#showVariantMedia(mediaId);
  };

  /**
   * @param {HTMLElement} option - An option input of the variant picker.
   * @returns {boolean} Whether the values of the option's group lead to more than one photo (a colour, not a size).
   */
  #optionChangesMedia(option) {
    const group = option.closest('fieldset, .variant-option');
    if (!group) return true;

    const media = new Set();
    for (const input of group.querySelectorAll('[data-variant-id]')) {
      if (!(input instanceof HTMLElement) || !input.dataset.variantId) continue;
      media.add(this.#mediaIdOfVariant(input.dataset.variantId) ?? '');
      if (media.size > 1) return true;
    }
    return false;
  }

  /**
   * @param {string} variantId - The id of a variant.
   * @returns {string | undefined} The id of the variant's photo, from the map the variant picker block renders.
   */
  #mediaIdOfVariant(variantId) {
    const map = this.closest('.shopify-section, dialog')?.querySelector('script[data-variant-media]');
    if (!map) return undefined;

    try {
      const mediaId = JSON.parse(map.textContent || '{}')[variantId];
      return mediaId ? String(mediaId) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Handles a variant update event. When the new gallery holds the same photos as this one, only in another
   * order, this gallery is kept and slides to the variant's photo, if it has one. Replacing it re-renders every
   * image and moves that photo to the front, which reads as a page reload. A gallery with different photos (another
   * product, or other variants' media hidden) is still replaced.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (!event.detail.data.newProduct && this.presentation === 'carousel' && this.#hasSameMedia(newMediaGallery)) {
      const mediaId = event.detail.resource?.featured_media?.id;

      if (mediaId) {
        this.#showVariantMedia(String(mediaId));
      } else {
        this.#variantMediaId = '';
      }
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {Element} gallery - Another media gallery.
   * @returns {boolean} Whether it shows the same media as this gallery, in any order.
   */
  #hasSameMedia(gallery) {
    /** @param {Element} root */
    const mediaIds = (root) => {
      const media = root.querySelectorAll('slideshow-slide [data-media-id]');
      const ids = Array.from(media, (element) => element.getAttribute('data-media-id'));
      return ids.sort().join();
    };

    return mediaIds(this) === mediaIds(gallery);
  }

  /**
   * Selects the slide showing the given media, unless it is the photo already selected for the variant, so a
   * size change does not pull the customer back from a photo they swiped to. Only the carousel slides; a grid
   * keeps the theme's own behaviour.
   *
   * @param {string} mediaId - The id of the media to show.
   */
  #showVariantMedia(mediaId) {
    if (this.presentation !== 'carousel' || this.#slideIndex(mediaId) === -1) return;

    if (mediaId !== this.#variantMediaId) {
      this.#variantMediaId = mediaId;
      this.#slideQueue = this.#slideQueue.then(() => this.#slideTo(mediaId)).catch((error) => console.error(error));
    }
  }

  /** Slide moves run one after another: the slideshow ignores a select while an animated jump is still running. */
  #slideQueue = Promise.resolve();

  /**
   * @param {string} mediaId - The id of the media to show.
   */
  async #slideTo(mediaId) {
    // After quick successive clicks, only the last choice is worth animating to.
    if (mediaId !== this.#variantMediaId) return;

    // Let the variant picker finish updating the page first. Started in the middle of that, the slideshow's
    // animated jump across several slides can stop part-way between two photos.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const index = this.#slideIndex(mediaId);
    const { slideshow } = this;
    if (index === -1 || !slideshow) return;

    // The corrections below must never undo the customer: once they touch the gallery, it is theirs.
    let touched = false;
    const onTouch = () => (touched = true);
    const touchEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
    for (const type of touchEvents) this.addEventListener(type, onTouch, { capture: true, passive: true });
    const stopListening = () => {
      for (const type of touchEvents) this.removeEventListener(type, onTouch, { capture: true });
    };
    const mayCorrect = () => !touched && mediaId === this.#variantMediaId && slideshow.isConnected;

    try {
      await slideshow.select(index);
      await this.#slideshowSettled();

      // A page busy with other updates can still leave the jump short of its photo. Put it in place without the
      // animation, rather than leave the customer looking at half of the wrong photo.
      if (mayCorrect() && slideshow.current !== index) {
        await slideshow.select(index, undefined, { animate: false });
        await this.#slideshowSettled();
      }

      if (mayCorrect() && slideshow.current === index) this.#alignSlide(index);
    } catch (error) {
      stopListening();
      throw error;
    }

    // The slideshow can still shift once more after coming to rest. Check again a little later, without holding up
    // the next slide move.
    setTimeout(() => {
      stopListening();
      if (mayCorrect() && slideshow.current === index) this.#alignSlide(index);
    }, 600);
  }

  /**
   * Scrolls the slide with the given index flush with the start of the slideshow, if it is not.
   *
   * @param {number} index - The index of the slide.
   */
  #alignSlide(index) {
    const slide = this.slideshow?.slides?.[index];
    const scroller = this.querySelector('slideshow-slides');
    if (!slide || !scroller) return;

    const offset = slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    if (Math.abs(offset) > 1) scroller.scrollBy({ left: offset, behavior: 'instant' });
  }

  /**
   * Resolves once the slideshow has stopped scrolling and has put back the placeholder slide it uses for an
   * animated jump across several slides.
   */
  async #slideshowSettled() {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    let lastScroll = performance.now();
    const onScroll = () => (lastScroll = performance.now());
    scroller.addEventListener('scroll', onScroll, { passive: true });

    try {
      for (let waited = 0; waited < 2000; waited += 50) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        const placeholder = this.querySelector('slideshow-slide:not([ref])');
        if (!placeholder && performance.now() - lastScroll > 150) return;
      }
    } finally {
      scroller.removeEventListener('scroll', onScroll);
    }
  }

  /**
   * @param {string} mediaId - The id of the media to find.
   * @returns {number} The index of the slide showing the media, or -1.
   */
  #slideIndex(mediaId) {
    return this.slideshow?.slides?.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`)) ?? -1;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
